import React from 'react';
import { segmentStyle } from './ServerList';

// Add/Edit Server Modal (step-based: pick connection type, then fill form)
export function ServerFormModal({
  showModal,
  editingServer,
  addStep,
  setAddStep,
  formData,
  setFormData,
  formError,
  submitting,
  onSubmit,
  onClose,
  onInputChange,
}) {
  if (!showModal) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingServer ? 'Edit Server' : 'Add Server'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Step 1: pick connection type (only on Add) */}
        {!editingServer && addStep === 'pick_type' ? (
          <div className="modal-body">
            <p style={{ color: 'var(--muted)', marginBottom: 20, fontSize: 14 }}>
              How will SarvKube connect to this server?
            </p>
            <div style={{ display: 'flex', gap: 16 }}>
              {[
                {
                  type: 'agent',
                  title: 'Install Agent',
                  desc: 'Recommended. Run a one-line install command on the server. No firewall changes, no keys stored.',
                  badge: 'Recommended',
                  badgeColor: 'var(--color-success-500)',
                },
                {
                  type: 'ssh',
                  title: 'SSH Key',
                  desc: 'Paste a private SSH key. Server must allow inbound SSH from the SarvKube API.',
                  badge: null,
                },
              ].map(opt => (
                <div
                  key={opt.type}
                  onClick={() => {
                    setFormData(p => ({ ...p, connection_type: opt.type }));
                    setAddStep('form');
                  }}
                  style={{
                    flex: 1, padding: 18, borderRadius: 8, cursor: 'pointer',
                    border: '2px solid var(--border)', position: 'relative',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  {opt.badge && (
                    <span style={{
                      position: 'absolute', top: -10, left: 12,
                      background: opt.badgeColor, color: '#fff',
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                    }}>{opt.badge}</span>
                  )}
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink-2)', marginBottom: 8 }}>{opt.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>{opt.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            </div>
          </div>
        ) : (
          /* Step 2: fill in server details */
          <form onSubmit={onSubmit}>
            <div className="modal-body">
              {formError && <div className="error-message">{formError}</div>}

              {/* Connection type indicator */}
              {!editingServer && (
                <div style={{ marginBottom: 16, padding: '8px 12px', borderRadius: 6, background: formData.connection_type === 'agent' ? 'var(--brand-soft)' : 'var(--bg)', border: '1px solid var(--color-brand-200)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-strong)' }}>
                    {formData.connection_type === 'agent' ? '🤖 Agent connection' : '🔑 SSH key connection'}
                  </span>
                  <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--brand)' }}
                    onClick={() => setAddStep('pick_type')}>Change</button>
                </div>
              )}

              <div className="form-group">
                <label>Server Name *</label>
                <input type="text" name="name" value={formData.name} onChange={onInputChange}
                  required disabled={!!editingServer} placeholder="e.g., voip-server-01" />
                {editingServer && <small style={{ color: 'var(--muted)' }}>Name cannot be changed</small>}
              </div>

              <div className="form-group">
                <label>Segment</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  {['voip', 'database', 'web', 'network', 'storage', 'kubernetes', 'other'].map(seg => (
                    <button key={seg} type="button"
                      onClick={() => setFormData(p => ({ ...p, segment: p.segment === seg ? '' : seg }))}
                      style={{
                        padding: '4px 14px', borderRadius: 20, border: `1px solid ${segmentStyle(seg).border}`,
                        background: formData.segment === seg ? segmentStyle(seg).bg : 'var(--surface)',
                        color: formData.segment === seg ? segmentStyle(seg).text : 'var(--ink-2)',
                        cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      }}>{seg}</button>
                  ))}
                </div>
                <input type="text" name="segment" value={formData.segment} onChange={onInputChange}
                  placeholder="Or type a custom segment name..." style={{ fontSize: 13 }} />
              </div>

              <div className="form-group">
                <label>IP Address {formData.connection_type === 'agent' ? '(optional — agent will self-report)' : '*'}</label>
                <input type="text" name="ip_address" value={formData.ip_address}
                  onChange={onInputChange} required={formData.connection_type === 'ssh'}
                  placeholder="e.g., 192.168.1.100" />
              </div>

              <div className="form-group">
                <label>Hostname</label>
                <input type="text" name="hostname" value={formData.hostname}
                  onChange={onInputChange} placeholder="e.g., server1.example.com" />
              </div>

              {/* SSH-only fields */}
              {formData.connection_type === 'ssh' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>SSH User *</label>
                      <input type="text" name="ssh_user" value={formData.ssh_user} onChange={onInputChange} required />
                    </div>
                    <div className="form-group">
                      <label>SSH Port *</label>
                      <input type="number" name="ssh_port" value={formData.ssh_port} onChange={onInputChange} required />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>SSH Private Key {!editingServer && '*'}</label>
                    <textarea name="ssh_private_key" value={formData.ssh_private_key}
                      onChange={onInputChange} required={!editingServer && formData.connection_type === 'ssh'} rows="5"
                      placeholder={editingServer ? 'Leave empty to keep existing key' : 'Paste your SSH private key...'} />
                    {(formData.ssh_private_key || '').trimStart().match(/^(ssh-|ecdsa-)/) && (
                      <div style={{color:'var(--color-danger-500)',fontSize:12,marginTop:4}}>
                        This looks like a public key (starts with ssh-rsa / ssh-ed25519 / ecdsa-). Please paste the <strong>private</strong> key instead — it should start with <code>-----BEGIN ... PRIVATE KEY-----</code>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Agent scope picker */}
              {formData.connection_type === 'agent' && (
                <div className="form-group">
                  <label>Agent Permission Scope</label>
                  <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                    {[
                      { value: 'read', icon: '👁', label: 'Read Only', desc: 'View metrics, logs, process list', color: 'var(--brand-strong)', bg: 'var(--brand-soft)', border: 'var(--color-brand-200)' },
                      { value: 'write', icon: '✏', label: 'Read + Write', desc: 'Run commands, restart services', color: 'var(--color-warning-700)', bg: 'var(--color-warning-50)', border: 'var(--color-warning-500)' },
                    ].map(opt => {
                      const selected = (formData.agent_scope || 'read') === opt.value;
                      return (
                        <div key={opt.value}
                          onClick={() => setFormData(p => ({ ...p, agent_scope: opt.value }))}
                          style={{
                            flex: 1, padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
                            border: `2px solid ${selected ? opt.border : 'var(--border)'}`,
                            background: selected ? opt.bg : 'var(--bg)',
                            transition: 'all 0.15s',
                          }}
                        >
                          <div style={{ fontSize: 18, marginBottom: 4 }}>{opt.icon}</div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: selected ? opt.color : 'var(--ink-2)' }}>{opt.label}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3, lineHeight: 1.4 }}>{opt.desc}</div>
                        </div>
                      );
                    })}
                  </div>
                  {editingServer && (
                    <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
                      Changing scope here will update the running agent on next connect (no reinstall needed).
                    </p>
                  )}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Provider</label>
                  <select name="provider" value={formData.provider} onChange={onInputChange}>
                    <option value="">Select provider</option>
                    <option value="aws">AWS</option>
                    <option value="gcp">Google Cloud</option>
                    <option value="azure">Azure</option>
                    <option value="digitalocean">DigitalOcean</option>
                    <option value="bare-metal">Bare Metal</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Region</label>
                  <input type="text" name="region" value={formData.region}
                    onChange={onInputChange} placeholder="e.g., us-east-1" />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : editingServer ? 'Update Server' : formData.connection_type === 'agent' ? 'Add & Get Install Cmd' : 'Add Server'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Agent Install Command Modal
export function AgentInstallModal({ agentModal, onClose }) {
  if (!agentModal) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>Install Agent on {agentModal.server_name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 12 }}>
            <span style={{
              padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 700,
              background: agentModal.scope === 'write' ? 'var(--accent-soft)' : 'var(--color-brand-200)',
              color: agentModal.scope === 'write' ? 'var(--accent)' : 'var(--brand-strong)',
            }}>Scope: {agentModal.scope}</span>
          </div>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>
            Run this command as <strong>root</strong> on <strong>{agentModal.server_name}</strong>:
          </p>
          <div style={{
            background: '#1a202c', color: '#68d391', padding: '12px 14px', borderRadius: 6,
            fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', marginBottom: 12,
            position: 'relative',
          }}>
            {agentModal.install_cmd}
            <button
              onClick={() => navigator.clipboard.writeText(agentModal.install_cmd)}
              style={{ position: 'absolute', top: 8, right: 8, fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#2d3748', color: '#a0aec0', border: 'none', cursor: 'pointer' }}
            >Copy</button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--muted)' }}>
            The agent will connect back automatically. The server status will show <strong>Agent ●</strong> once connected.
            You can change the scope or rotate this token anytime from the server table.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

// Unregistered IPs floating notification panel
export function UnregisteredIPsPanel({ unregisteredIPs, onDismiss, onAddIP, EMPTY_FORM }) {
  if (unregisteredIPs.length === 0) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, background: 'var(--surface)',
      border: '1px solid var(--accent-soft)', borderRadius: 10, padding: '14px 16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.12)', maxWidth: 320, zIndex: 100,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--accent)' }}>
          ⚠ {unregisteredIPs.length} IPs firing alerts
        </span>
        <button
          onClick={onDismiss}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-2)', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
          title="Dismiss"
        >×</button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
        These IPs appear in alerts but aren't in your server inventory.
      </div>
      {unregisteredIPs.slice(0, 4).map(item => (
        <div key={item.ip} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '5px 0', borderBottom: '1px solid var(--border)',
        }}>
          <div>
            <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink-2)', fontWeight: 600 }}>{item.ip}</span>
            <span style={{ fontSize: 11, color: 'var(--muted-2)', marginLeft: 6 }}>{item.alert_count} alerts</span>
          </div>
          <button
            className="btn btn-sm btn-secondary"
            style={{ color: 'var(--brand)' }}
            onClick={() => onAddIP(item.ip)}
          >+ Add</button>
        </div>
      ))}
      {unregisteredIPs.length > 4 && (
        <div style={{ fontSize: 11, color: 'var(--muted-2)', marginTop: 6 }}>
          +{unregisteredIPs.length - 4} more IPs not shown
        </div>
      )}
    </div>
  );
}
